import React, { useEffect, useMemo, useState } from "react";
import { workerTerm } from "../utils/workerTerm";

// 관리자 → 소속 기사(설치팀장/작업자) 공지·메시지 발송.
//   백엔드 공용 API(/api/notice/*)를 호출 — 동일 API를 모니티 앱도 사용한다.
export default function NoticeSenderPage({ me }) {
  const role = String(me?.roleCode || "").toUpperCase();
  const canCompany = role === "SUPER_ADMIN" || role === "ADMIN" || role === "HQ_MANAGER";
  const term = workerTerm(); // 삼우=설치팀장 / 그 외=작업자

  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [myBranch, setMyBranch] = useState(null);

  const [scope, setScope] = useState("branch"); // branch | company | users
  const [branch, setBranch] = useState("");      // scope=branch 선택 지점(본사 이상)
  const [selected, setSelected] = useState({});  // userIdx -> true
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}
  const [history, setHistory] = useState([]);

  const loadTargets = async () => {
    try {
      const r = await fetch("/api/notice/targets", { credentials: "include" });
      const d = await r.json();
      if (d.success) {
        setWorkers(d.workers || []);
        setBranches(d.branches || []);
        setMyBranch(d.branch || null);
        if (d.branch) setBranch(d.branch);
      }
    } catch (_) {}
    setLoading(false);
  };
  const loadHistory = async () => {
    try {
      const r = await fetch("/api/notice/history?limit=10", { credentials: "include" });
      const d = await r.json();
      if (d.success) setHistory(d.notices || []);
    } catch (_) {}
  };

  useEffect(() => { loadTargets(); loadHistory(); }, []);

  // scope=branch 이고 본사 이상이면 지점 필터, 그 외엔 전체 목록
  const shownWorkers = useMemo(() => {
    if (scope === "branch" && canCompany && branch) {
      return workers.filter((w) => (w.BranchName || "") === branch);
    }
    return workers;
  }, [workers, scope, branch, canCompany]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const targetSummary = () => {
    if (scope === "company") return `회사 전체 ${term} (${workers.length}명)`;
    if (scope === "users") return `선택한 ${term} ${selectedCount}명`;
    // branch
    const b = canCompany ? branch : myBranch;
    const n = shownWorkers.length;
    return b ? `${b} 지점 ${term} (${n}명)` : `소속 ${term} 전체 (${n}명)`;
  };

  const send = async () => {
    setMsg(null);
    if (!title.trim() && !body.trim()) { setMsg({ type: "err", text: "제목 또는 내용을 입력하세요." }); return; }
    if (scope === "users" && selectedCount === 0) { setMsg({ type: "err", text: `대상 ${term}를 1명 이상 선택하세요.` }); return; }
    const payload = {
      scope,
      title: title.trim(),
      body: body.trim(),
      branch: scope === "branch" ? (canCompany ? branch : undefined) : undefined,
      userIdxs: scope === "users" ? Object.keys(selected).filter((k) => selected[k]).map(Number) : undefined,
    };
    setSending(true);
    try {
      const r = await fetch("/api/notice/send", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.success) {
        const pushed = d.pushed != null ? d.pushed : d.recipients;
        let text = `공지 발송 완료 — 대상 ${d.recipients}명`;
        if (d.recipients === 0) text += " (대상 기사가 없습니다)";
        else if (pushed === 0) text += " · 공지함엔 저장됨(등록된 푸시 기기 없어 실시간 푸시 0건)";
        else text += ` · 푸시 ${pushed}건`;
        setMsg({ type: "ok", text });
        setTitle(""); setBody(""); setSelected({});
        loadHistory();
      } else {
        setMsg({ type: "err", text: d.message || "발송 실패" });
      }
    } catch (e) {
      setMsg({ type: "err", text: "네트워크 오류: " + String(e) });
    }
    setSending(false);
  };

  const radioClass = (v) =>
    `flex-1 cursor-pointer rounded-xl border px-4 py-3 text-sm font-semibold transition ${
      scope === v
        ? "border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-400/10 dark:text-blue-200"
        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5"
    }`;

  return (
    <div className="bg-white dark:bg-[#1F2A3B] p-4 sm:p-6 rounded-xl shadow-md min-w-0">
      <h3 className="text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-200 mb-1">
        공지 발송
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        소속 {term}에게 공지·알림을 보냅니다. 대상은 지점 전체 / 회사 전체 / 특정 {term}로 선택할 수 있습니다.
      </p>

      {loading ? (
        <div className="py-16 text-center text-gray-400">불러오는 중…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 작성 영역 */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {/* 대상 범위 */}
            <div>
              <div className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-300">대상 범위</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button type="button" className={radioClass("branch")} onClick={() => setScope("branch")}>
                  {canCompany ? "지점 전체" : `소속 ${term} 전체`}
                </button>
                {canCompany && (
                  <button type="button" className={radioClass("company")} onClick={() => setScope("company")}>
                    회사 전체
                  </button>
                )}
                <button type="button" className={radioClass("users")} onClick={() => setScope("users")}>
                  특정 {term} 선택
                </button>
              </div>
            </div>

            {/* 지점 선택(본사 이상 & branch) */}
            {scope === "branch" && canCompany && branches.length > 0 && (
              <div>
                <div className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-300">지점</div>
                <select
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-white/10 dark:bg-[#111827] dark:text-gray-100"
                >
                  <option value="">전체 지점</option>
                  {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            )}

            {/* 기사 다중선택(users) */}
            {scope === "users" && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">{term} 선택 ({selectedCount})</span>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline dark:text-blue-300"
                    onClick={() => {
                      const all = {}; shownWorkers.forEach((w) => { all[w.UserIdx] = true; });
                      setSelected(selectedCount === shownWorkers.length ? {} : all);
                    }}
                  >
                    {selectedCount === shownWorkers.length && shownWorkers.length > 0 ? "전체 해제" : "전체 선택"}
                  </button>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/5">
                  {shownWorkers.length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-gray-400">등록된 {term}가 없습니다.</div>
                  )}
                  {shownWorkers.map((w) => (
                    <label key={w.UserIdx} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={!!selected[w.UserIdx]}
                        onChange={(e) => setSelected((s) => ({ ...s, [w.UserIdx]: e.target.checked }))}
                        className="h-4 w-4"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-200">{w.UserName}</span>
                      {w.BranchName && <span className="text-xs text-gray-400">{w.BranchName}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 제목/내용 */}
            <div>
              <div className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-300">제목</div>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
                placeholder="예: 오늘 안전점검 필수 확인"
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-white/10 dark:bg-[#111827] dark:text-gray-100"
              />
            </div>
            <div>
              <div className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-300">내용</div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={500}
                rows={5}
                placeholder="공지 내용을 입력하세요."
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-white/10 dark:bg-[#111827] dark:text-gray-100"
              />
            </div>

            {msg && (
              <div className={`rounded-xl px-4 py-3 text-sm ${
                msg.type === "ok"
                  ? "bg-green-50 text-green-700 dark:bg-green-400/10 dark:text-green-300"
                  : "bg-red-50 text-red-700 dark:bg-red-400/10 dark:text-red-300"
              }`}>
                {msg.text}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                대상: <span className="font-semibold text-gray-700 dark:text-gray-200">{targetSummary()}</span>
              </div>
              <button
                type="button"
                onClick={send}
                disabled={sending}
                className="rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {sending ? "발송 중…" : "공지 발송"}
              </button>
            </div>
          </div>

          {/* 최근 공지 이력 */}
          <div className="lg:col-span-1">
            <div className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-300">최근 공지</div>
            <div className="flex flex-col gap-2">
              {history.length === 0 && (
                <div className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-400 dark:border-white/10">
                  발송 이력이 없습니다.
                </div>
              )}
              {history.map((h) => (
                <div key={h.NoticeIdx} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 dark:border-white/5 dark:bg-white/5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{h.Title || "(제목 없음)"}</span>
                    <span className="text-[11px] text-gray-400 shrink-0 ml-2">{h.RecipientCount}명</span>
                  </div>
                  {h.Body && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{h.Body}</div>}
                  <div className="mt-1 text-[11px] text-gray-400">
                    {h.SenderName} · {h.BranchName || h.CompanyName || ""} · {h.createdAt}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
